import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Check, Circle, Save, Target, Zap, Send,
  Phone, Mail, Linkedin, ExternalLink,
  ChevronDown, ChevronUp, Database, Activity,
  Layers, Clock, BarChart2, FileText
} from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Label } from '@/components/common/Label';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import * as accountsService from '@/services/api/accountsService';
import * as contactsService from '@/services/api/contactsService';
import apiClient from '@/services/api/apiClient';
import SearchableSelect from '@/components/common/SearchableSelect';
import { SuggestedField } from './SuggestedField';
import { AgentRunBar, useStageAgent } from './StageAgentHook';
import CallStagePanel from './CallStagePanel';
import StageRemarks from './StageRemarks';
import EngagementScoreWidget from './EngagementScoreWidget';
import SendMagnetModal from '@/features/communication/SendMagnetModal';

// ── Stage metadata ────────────────────────────────────────────────────────────
const JOURNEY_STAGES = [
  { id: 1,  title: 'Account & Contact Context',  desc: 'Identify target account, role, industry, plant type, trigger', phase: 'Discovery'  },
  { id: 2,  title: 'Maturity Mirror Exposure',    desc: 'Share neutral execution maturity model',                       phase: 'Discovery'  },
  { id: 3,  title: 'Role-Based Pain Framing',     desc: 'Present industry-specific pain narratives',                    phase: 'Discovery'  },
  { id: 4,  title: 'Maturity Gap Awareness',      desc: 'Highlight common breakdowns as plants scale',                  phase: 'Discovery'  },
  { id: 5,  title: 'MES Reality Alignment',       desc: 'Clarify ERP, MES, and reporting tools differences',            phase: 'Discovery'  },
  { id: 6,  title: 'Alignment Call #1',           desc: 'Validate pain direction and execution challenges',              phase: 'Advisory'   },
  { id: 7,  title: 'Architecture Thinking',       desc: 'Central vs edge execution concepts',                            phase: 'Advisory'   },
  { id: 8,  title: 'Multi-System Coexistence',    desc: 'How ERP, MES, QMS, PLC coexist',                              phase: 'Advisory'   },
  { id: 9,  title: 'Data Readiness',              desc: 'Data trust, context, readiness',                                phase: 'Education'  },
  { id: 10, title: 'Automation Timing',           desc: 'What to automate now vs later',                                 phase: 'Education'  },
  { id: 11, title: 'Decision Intelligence',       desc: 'Analytics as decision support',                                 phase: 'Education'  },
  { id: 12, title: 'Confirmation Call #2',        desc: 'Validate system direction, buying group',                       phase: 'Validation' },
  { id: 13, title: 'Future-State Snapshot',       desc: 'Realistic 90-day improvement vision',                           phase: 'Validation' },
  { id: 14, title: 'MQL Validation & Handoff',    desc: 'Sales-ready MQL brief',                                        phase: 'Validation' },
];

const PARTNER_JOURNEY_STAGES = [
  { id: 1,  title: 'Partner & Contact Context',      desc: 'Identify partner firm, role, market served, current portfolio', phase: 'Discovery'  },
  { id: 2,  title: 'Portfolio Gap Exposure',         desc: 'Where their current line leaves demand unmet',                  phase: 'Discovery'  },
  { id: 3,  title: 'Market Demand Framing',          desc: 'End-customer MES demand in their territory',                    phase: 'Discovery'  },
  { id: 4,  title: 'Margin & Revenue Opportunity',   desc: 'Reseller margin, recurring revenue, deal-size upside',          phase: 'Discovery'  },
  { id: 5,  title: 'Product Fit & Differentiation',  desc: 'Why Cantier MES vs what they sell today',                       phase: 'Discovery'  },
  { id: 6,  title: 'Partnership Alignment Call #1',   desc: 'Validate appetite, territory, deal model',                     phase: 'Advisory'   },
  { id: 7,  title: 'Enablement & Delivery Model',     desc: 'Sales/technical enablement, delivery capacity',                phase: 'Advisory'   },
  { id: 8,  title: 'Commercial & Channel Terms',      desc: 'Discount tiers, MDF, deal registration, exclusivity',         phase: 'Advisory'   },
  { id: 9,  title: 'Lead & Pipeline Readiness',       desc: 'Their existing pipeline that fits Cantier MES',               phase: 'Education'  },
  { id: 10, title: 'Go-To-Market Timing',             desc: 'Quick wins now vs build later',                               phase: 'Education'  },
  { id: 11, title: 'Joint Business Planning',         desc: 'Targets, co-marketing, quarterly commitments',                phase: 'Education'  },
  { id: 12, title: 'Partnership Confirmation Call #2', desc: 'Confirm territory, terms, first joint deals',                phase: 'Validation' },
  { id: 13, title: 'Onboarding & Ramp Snapshot',      desc: '90-day partner activation and first-deal plan',               phase: 'Validation' },
  { id: 14, title: 'PQL Validation & Handoff',        desc: 'Partner-qualified — ready for channel agreement',             phase: 'Validation' },
];

const PHASE_COLORS = { Discovery: 'tier1', Advisory: 'tier2', Education: 'active', Validation: 'mqlReady' } as const;

// ── Per-stage field → agent key mapping ──────────────────────────────────────
// Each stage form field declares which agent output keys are suggestions for it
const STAGE_FIELD_SUGGESTIONS: Record<number, Record<string, { agentKey: string; label: string }[]>> = {
  1: {
    trigger:    [{ agentKey: 'triggerStatement', label: 'Why reach out now' }, { agentKey: 'linkedInHook', label: 'LinkedIn hook' }],
    activities: [{ agentKey: 'emailOpener', label: 'Email opener' }],
    notes:      [{ agentKey: 'qualifyingQuestions', label: 'Qualifying questions' }],
  },
  2: {
    activities: [{ agentKey: 'framingIntro', label: 'Framing intro' }],
    notes:      [{ agentKey: 'proofPoint', label: 'Proof point' }, { agentKey: 'magnetSuggestion', label: 'Magnet to send' }],
    response:   [{ agentKey: 'reflectionQuestions', label: 'Reflection questions' }],
  },
  3: {
    activities: [{ agentKey: 'painNarrative', label: 'Pain narrative (their voice)' }],
    response:   [{ agentKey: 'probeQuestion', label: 'Probe question to ask' }],
    notes:      [{ agentKey: 'resonanceStatement', label: 'Resonance statement' }, { agentKey: 'industryExamples', label: 'Industry examples' }],
  },
  4: {
    activities: [{ agentKey: 'gapDescription', label: 'Gap description' }],
    notes:      [{ agentKey: 'costFraming', label: 'Cost framing' }, { agentKey: 'breakdownScenarios', label: 'Breakdown scenarios' }],
    nextSteps:  [{ agentKey: 'nextStepChallenge', label: 'Challenge question' }],
  },
  5: {
    activities: [{ agentKey: 'stackExplanation', label: 'Stack explanation' }],
    notes:      [{ agentKey: 'blindSpots', label: 'Stack blind spots' }, { agentKey: 'cantierAngle', label: 'Cantier fit angle' }],
    response:   [{ agentKey: 'revealQuestion', label: 'MES gap reveal question' }],
  },
  6: {
    activities: [{ agentKey: 'callOpening', label: '60-sec call opening' }, { agentKey: 'discoveryQuestions', label: 'Discovery questions' }],
    notes:      [{ agentKey: 'hypotheses', label: 'Hypotheses to validate' }, { agentKey: 'successCriteria', label: 'Call success criteria' }],
    nextSteps:  [{ agentKey: 'objectionHandles', label: 'Objection handles' }],
  },
  7: {
    activities: [{ agentKey: 'architectureFraming', label: 'Edge vs central framing' }],
    notes:      [{ agentKey: 'stackDiagnosis', label: 'Stack diagnosis' }, { agentKey: 'cantierPosition', label: 'Cantier position' }],
    response:   [{ agentKey: 'architectureQuestion', label: 'Architecture mapping question' }],
  },
  8: {
    activities: [{ agentKey: 'coexistenceChallenge', label: 'Coexistence challenge' }, { agentKey: 'failureModes', label: 'Failure modes' }],
    notes:      [{ agentKey: 'cantierSolution', label: 'Cantier solution' }, { agentKey: 'proofClaim', label: 'Proof claim' }],
  },
  9: {
    activities: [{ agentKey: 'readinessAssessment', label: 'Readiness assessment' }],
    notes:      [{ agentKey: 'dataGaps', label: 'Data gaps blocking them' }],
    nextSteps:  [{ agentKey: 'readinessMilestone', label: 'Data-ready milestone' }],
    response:   [{ agentKey: 'dataQuestion', label: 'Decision-making question' }],
  },
  10: {
    activities: [{ agentKey: 'automateFirst', label: 'What to automate first' }],
    notes:      [{ agentKey: 'deferReason', label: 'What to defer + why' }, { agentKey: 'roiFraming', label: 'ROI framing' }],
    nextSteps:  [{ agentKey: 'automationSequence', label: '3-step automation plan' }],
  },
  11: {
    activities: [{ agentKey: 'executiveSummary', label: 'Executive summary' }, { agentKey: 'blindDecisions', label: 'Decisions they make blind' }],
    notes:      [{ agentKey: 'cantierClose', label: 'Cantier 90-day close' }, { agentKey: 'analyticsROI', label: 'Analytics ROI' }],
  },
  12: {
    activities: [{ agentKey: 'callOpening', label: 'Call opening (refs Stage 6)' }, { agentKey: 'confirmationQuestions', label: 'Confirmation questions' }],
    notes:      [{ agentKey: 'buyingGroupQuestions', label: 'Buying group questions' }],
    nextSteps:  [{ agentKey: 'closeStatement', label: 'Close statement' }],
  },
  13: {
    activities: [{ agentKey: 'futureStateVision', label: '90-day future state vision' }],
    notes:      [{ agentKey: 'measurableOutcomes', label: 'Measurable outcomes' }, { agentKey: 'keyContrast', label: 'Before/after contrast' }],
    nextSteps:  [{ agentKey: 'workshopAgenda', label: 'Workshop agenda' }],
  },
  14: {
    activities: [{ agentKey: 'salesBrief', label: 'Sales handoff brief' }],
    notes:      [{ agentKey: 'risksToFlag', label: 'Risks to flag' }, { agentKey: 'dealSize', label: 'Deal size estimate' }, { agentKey: 'qualificationSignals', label: 'MQL qualification signals' }],
    nextSteps:  [{ agentKey: 'recommendedAction', label: 'Recommended next action' }],
  },
};

// ── Per-stage MQL qualification fields (BANT + MES-fit) ───────────────────────
// Stored under stageData[n].qual and scored by backend qualScoring.js.
type QualField =
  | { key: string; label: string; type: 'bool' }
  | { key: string; label: string; type: 'select'; options: { v: string; l: string }[] }
  | { key: string; label: string; type: 'number'; placeholder?: string }
  | { key: string; label: string; type: 'date' };

const QUAL_FIELDS: Record<number, { dim: string; fields: QualField[] }> = {
  1: { dim: 'Timing · Authority', fields: [
    { key: 'triggerConfirmed', label: 'Trigger event confirmed', type: 'bool' },
    { key: 'contactIsDecisionMaker', label: 'Contact role', type: 'select', options: [
      { v: 'yes', l: 'Decision maker' }, { v: 'influencer', l: 'Influencer' }, { v: 'gatekeeper', l: 'Gatekeeper' }, { v: 'unknown', l: 'Unknown' } ] },
  ] },
  2: { dim: 'MES-fit (maturity)', fields: [
    { key: 'selfIdentifiedBand', label: 'Self-identified maturity band', type: 'select', options: [
      { v: 'L1', l: 'L1 — Manual' }, { v: 'L2', l: 'L2 — Basic digital' }, { v: 'L3', l: 'L3 — Connected' }, { v: 'L4', l: 'L4 — Analytics' }, { v: 'L5', l: 'L5 — AI-optimised' } ] },
    { key: 'bandAgreement', label: 'Band agreement', type: 'select', options: [
      { v: 'agreed', l: 'Agreed' }, { v: 'disputed', l: 'Disputed' }, { v: 'unknown', l: 'Unknown' } ] },
  ] },
  3: { dim: 'Need', fields: [
    { key: 'painConfirmed', label: 'Pain confirmed by contact', type: 'bool' },
    { key: 'confirmedPainAxis', label: 'Confirmed pain axis', type: 'select', options: [
      { v: 'VISIBILITY', l: 'Visibility' }, { v: 'EXECUTION', l: 'Execution' }, { v: 'DATA', l: 'Data' }, { v: 'QUALITY', l: 'Quality' }, { v: 'INTEGRATION', l: 'Integration' } ] },
    { key: 'painSeverity', label: 'Pain severity', type: 'select', options: [
      { v: 'low', l: 'Low' }, { v: 'med', l: 'Medium' }, { v: 'high', l: 'High' }, { v: 'critical', l: 'Critical' } ] },
  ] },
  4: { dim: 'Need · Budget seed', fields: [
    { key: 'gapAcknowledged', label: 'Gap acknowledged', type: 'bool' },
    { key: 'impactValueUsd', label: 'Quantified impact (USD/yr)', type: 'number', placeholder: 'e.g. 120000' },
  ] },
  5: { dim: 'MES-fit (core)', fields: [
    { key: 'hasMes', label: 'MES in place', type: 'select', options: [
      { v: 'none', l: 'None' }, { v: 'partial', l: 'Partial' }, { v: 'legacy', l: 'Legacy' }, { v: 'modern', l: 'Modern' } ] },
    { key: 'erpPresent', label: 'ERP present', type: 'bool' },
    { key: 'mesGapConfirmed', label: 'MES gap confirmed', type: 'bool' },
  ] },
  6: { dim: 'Need validation · Authority', fields: [
    { key: 'callHeld', label: 'Alignment call held', type: 'bool' },
    { key: 'painValidatedOnCall', label: 'Pain validated on call', type: 'bool' },
    { key: 'championIdentified', label: 'Champion identified', type: 'bool' },
  ] },
  7: { dim: 'MES-fit (technical)', fields: [
    { key: 'architectureMapped', label: 'Architecture mapped', type: 'bool' },
    { key: 'integrationComplexity', label: 'Integration complexity', type: 'select', options: [
      { v: 'low', l: 'Low' }, { v: 'med', l: 'Medium' }, { v: 'high', l: 'High' } ] },
  ] },
  8: { dim: 'MES-fit', fields: [
    { key: 'coexistencePainConfirmed', label: 'Coexistence pain confirmed', type: 'bool' },
  ] },
  9: { dim: 'MES-fit (data)', fields: [
    { key: 'dataReadinessLevel', label: 'Data readiness', type: 'select', options: [
      { v: 'none', l: 'None' }, { v: 'basic', l: 'Basic' }, { v: 'connected', l: 'Connected' }, { v: 'analytics', l: 'Analytics' } ] },
  ] },
  10: { dim: 'Timing', fields: [
    { key: 'automationPriority', label: 'Automation priority', type: 'select', options: [
      { v: 'now', l: 'Now' }, { v: 'next', l: 'Next' }, { v: 'later', l: 'Later' } ] },
    { key: 'initiativeExists', label: 'Funded initiative exists', type: 'bool' },
  ] },
  11: { dim: 'Authority · Risk', fields: [
    { key: 'decisionCriteriaKnown', label: 'Decision criteria known', type: 'bool' },
    { key: 'competitorsPresent', label: 'Competitors in the deal', type: 'bool' },
  ] },
  12: { dim: 'Authority · Budget', fields: [
    { key: 'buyingGroupMapped', label: 'Buying group mapped', type: 'bool' },
    { key: 'economicBuyerReachable', label: 'Economic buyer reachable', type: 'bool' },
    { key: 'budgetSignal', label: 'Budget signal', type: 'select', options: [
      { v: 'none', l: 'None' }, { v: 'exploring', l: 'Exploring' }, { v: 'allocated', l: 'Allocated' }, { v: 'approved', l: 'Approved' } ] },
  ] },
  13: { dim: 'Timing (hard date)', fields: [
    { key: 'visionAgreed', label: 'Future-state vision agreed', type: 'bool' },
    { key: 'targetGoLive', label: 'Target go-live date', type: 'date' },
    { key: 'successMetricsAgreed', label: 'Success metrics agreed', type: 'bool' },
  ] },
  14: { dim: 'Final MQL gate', fields: [
    { key: 'dealSizeUsd', label: 'Deal size (USD)', type: 'number', placeholder: 'e.g. 250000' },
    { key: 'mqlDecision', label: 'MQL decision', type: 'select', options: [
      { v: 'qualified', l: 'Qualified — count as MQL' }, { v: 'nurture', l: 'Nurture' }, { v: 'disqualified', l: 'Disqualified' } ] },
  ] },
};

// ── PARTNER qualification fields (appetite/territory/terms/delivery/first-deals, not BANT) ──
// Stored under stageData[n].qual and scored by backend qualScoring.js (PQL path).
const PARTNER_QUAL_FIELDS: Record<number, { dim: string; fields: QualField[] }> = {
  1: { dim: 'Appetite · Authority', fields: [
    { key: 'partnershipInterest', label: 'Partnership interest expressed', type: 'bool' },
    { key: 'contactIsDecisionMaker', label: 'Contact role', type: 'select', options: [
      { v: 'owner', l: 'Owner / Principal' }, { v: 'yes', l: 'Decision maker' }, { v: 'influencer', l: 'Influencer' }, { v: 'unknown', l: 'Unknown' } ] },
  ] },
  2: { dim: 'Fit (portfolio)', fields: [
    { key: 'portfolioGapConfirmed', label: 'Portfolio gap confirmed', type: 'bool' },
    { key: 'currentPortfolio', label: 'Current portfolio vs MES', type: 'select', options: [
      { v: 'complementary', l: 'Complementary' }, { v: 'adjacent', l: 'Adjacent' }, { v: 'overlapping', l: 'Overlapping' }, { v: 'none', l: 'None yet' } ] },
  ] },
  3: { dim: 'Territory demand', fields: [
    { key: 'demandLevel', label: 'End-customer MES demand in territory', type: 'select', options: [
      { v: 'weak', l: 'Weak' }, { v: 'moderate', l: 'Moderate' }, { v: 'strong', l: 'Strong' } ] },
    { key: 'territoryDefined', label: 'Target territory defined', type: 'bool' },
  ] },
  4: { dim: 'Revenue appetite', fields: [
    { key: 'revenueMotivated', label: 'Motivated by margin / recurring revenue', type: 'bool' },
    { key: 'expectedDealSizeUsd', label: 'Typical end-deal size (USD)', type: 'number', placeholder: 'e.g. 150000' },
  ] },
  5: { dim: 'Fit (product)', fields: [
    { key: 'differentiationClear', label: 'Differentiation vs current line clear', type: 'bool' },
    { key: 'competesWithLine', label: 'Overlap with current line', type: 'select', options: [
      { v: 'no', l: 'No overlap' }, { v: 'complementary', l: 'Complementary' }, { v: 'partial', l: 'Partial overlap' }, { v: 'direct', l: 'Direct competitor' } ] },
  ] },
  6: { dim: 'Appetite validation · Authority', fields: [
    { key: 'callHeld', label: 'Alignment call held', type: 'bool' },
    { key: 'appetiteValidated', label: 'Appetite validated on call', type: 'bool' },
    { key: 'championIdentified', label: 'Internal champion identified', type: 'bool' },
  ] },
  7: { dim: 'Delivery capacity', fields: [
    { key: 'technicalCapacity', label: 'Technical / delivery capacity', type: 'select', options: [
      { v: 'none', l: 'None' }, { v: 'basic', l: 'Basic' }, { v: 'strong', l: 'Strong' } ] },
    { key: 'salesCapacity', label: 'Sales capacity', type: 'select', options: [
      { v: 'none', l: 'None' }, { v: 'basic', l: 'Basic' }, { v: 'strong', l: 'Strong' } ] },
  ] },
  8: { dim: 'Commercial terms', fields: [
    { key: 'termsDiscussed', label: 'Channel terms discussed', type: 'bool' },
    { key: 'dealRegistrationWanted', label: 'Wants deal registration', type: 'bool' },
    { key: 'exclusivityRequested', label: 'Exclusivity', type: 'select', options: [
      { v: 'none', l: 'None' }, { v: 'regional', l: 'Regional' }, { v: 'exclusive', l: 'Exclusive' } ] },
  ] },
  9: { dim: 'Pipeline readiness', fields: [
    { key: 'existingPipelineFit', label: 'Existing pipeline fits Cantier MES', type: 'bool' },
    { key: 'pipelineDealCount', label: 'Fitting deals in pipeline', type: 'number', placeholder: 'e.g. 3' },
  ] },
  10: { dim: 'GTM timing', fields: [
    { key: 'gtmPriority', label: 'Go-to-market priority', type: 'select', options: [
      { v: 'now', l: 'Now' }, { v: 'next', l: 'Next' }, { v: 'later', l: 'Later' } ] },
    { key: 'gtmInitiativeFunded', label: 'Funded GTM initiative exists', type: 'bool' },
  ] },
  11: { dim: 'Commitment', fields: [
    { key: 'jointTargetsAgreed', label: 'Joint targets agreed', type: 'bool' },
    { key: 'coMarketingInterest', label: 'Co-marketing interest', type: 'bool' },
  ] },
  12: { dim: 'Terms · Territory', fields: [
    { key: 'territoryConfirmed', label: 'Territory confirmed', type: 'bool' },
    { key: 'termsAgreed', label: 'Terms agreed', type: 'select', options: [
      { v: 'none', l: 'None' }, { v: 'verbal', l: 'Verbal' }, { v: 'written', l: 'Written' } ] },
    { key: 'firstDealsIdentified', label: 'First joint deals identified', type: 'bool' },
  ] },
  13: { dim: 'Ramp (hard date)', fields: [
    { key: 'activationPlanAgreed', label: 'Activation plan agreed', type: 'bool' },
    { key: 'targetFirstDeal', label: 'Target first-deal date', type: 'date' },
    { key: 'rampMetricsAgreed', label: 'Ramp metrics agreed', type: 'bool' },
  ] },
  14: { dim: 'Final PQL gate', fields: [
    { key: 'annualPartnerValueUsd', label: 'Est. annual partner value (USD)', type: 'number', placeholder: 'e.g. 500000' },
    { key: 'pqlDecision', label: 'PQL decision', type: 'select', options: [
      { v: 'qualified', l: 'Qualified — count as PQL' }, { v: 'nurture', l: 'Nurture' }, { v: 'disqualified', l: 'Disqualified' } ] },
  ] },
};

function QualFields({ stage, qual, onChange, isPartner }: { stage: number; qual: Record<string, any>; onChange: (q: Record<string, any>) => void; isPartner?: boolean; }) {
  const cfg = (isPartner ? PARTNER_QUAL_FIELDS : QUAL_FIELDS)[stage];
  if (!cfg) return null;
  const set = (k: string, v: any) => onChange({ ...qual, [k]: v });
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.03] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">{isPartner ? 'PQL qualification' : 'MQL qualification'}</span>
        <span className="text-xs font-medium text-primary">{cfg.dim}</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {cfg.fields.map((f) => (
          <div key={f.key}>
            {f.type === 'bool' ? (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!qual[f.key]} onChange={(e) => set(f.key, e.target.checked)}
                  className="h-4 w-4 rounded border-border" />
                {f.label}
              </label>
            ) : (
              <>
                <label className="text-xs font-medium text-muted-foreground block mb-1">{f.label}</label>
                {f.type === 'select' ? (
                  <select value={qual[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-sm">
                    <option value="">Select…</option>
                    {f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                ) : f.type === 'number' ? (
                  <input type="number" value={qual[f.key] ?? ''} placeholder={f.placeholder}
                    onChange={(e) => set(f.key, e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-sm" />
                ) : (
                  <input type="date" value={qual[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-sm" />
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const PAIN_PILL: Record<string, string> = {
  VISIBILITY: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  EXECUTION: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400',
  DATA: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400',
  QUALITY: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
  INTEGRATION: 'bg-teal-100 text-teal-700 dark:bg-teal-950/40 dark:text-teal-400',
};

function PainPill({ axis }: { axis?: string }) {
  if (!axis) return null;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PAIN_PILL[axis] || 'bg-muted text-muted-foreground'}`}>{axis}</span>;
}
function MaturityPill({ band }: { band?: string }) {
  if (!band) return null;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">{band}</span>;
}
function IcpPill({ score }: { score?: number | null }) {
  if (score == null) return null;
  const grade = score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D';
  const col = grade === 'A' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400' :
              grade === 'B' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400' :
                              'bg-muted text-muted-foreground';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${col}`}>ICP {grade} · {score}</span>;
}
function TagRow({ tags, mono }: { tags?: string[]; mono?: boolean }) {
  if (!tags?.length) return null;
  return <div className="flex flex-wrap gap-1">{tags.map((t, i) => <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground border border-border ${mono ? 'font-mono' : ''}`}>{t}</span>)}</div>;
}
function SignalList({ signals, label = 'Signals' }: { signals?: string[]; label?: string }) {
  if (!signals?.length) return null;
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
      <div className="flex flex-wrap gap-1">{signals.slice(0, 3).map((s, i) => <span key={i} className="px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-xs">{s}</span>)}</div>
    </div>
  );
}
function CasePills({ cases }: { cases?: string[] }) {
  if (!cases?.length) return null;
  return <div className="flex flex-wrap gap-1">{cases.map((c, i) => <span key={i} className="flex items-center gap-0.5 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs border border-primary/20"><Zap className="h-2.5 w-2.5" />{c}</span>)}</div>;
}

// ── Pending MDA suggestions (review / accept / dismiss) ───────────────────────
function PendingSuggestionsBanner({ journeyId, onApplied, addToast }: { journeyId: string; onApplied: () => void; addToast: any }) {
  const [pending, setPending] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    if (!journeyId) { setPending([]); return; }
    try {
      const { data } = await apiClient.get(`/api/abm-journeys/${journeyId}/pending`);
      setPending(data.pending || []);
    } catch { setPending([]); }
  };
  useEffect(() => { load(); }, [journeyId]);

  if (!journeyId || pending.length === 0) return null;

  const act = async (id: string, action: 'accept' | 'reject') => {
    setBusy(id + action);
    try {
      await apiClient.post(`/api/abm-journeys/${journeyId}/pending/${id}/${action}`);
      addToast({
        type: 'success',
        title: action === 'accept' ? 'Suggestion applied' : 'Suggestion dismissed',
        description: action === 'accept' ? 'The journey was updated.' : 'No changes were made.',
      });
      await load();
      if (action === 'accept') onApplied();
    } catch (e: any) {
      addToast({ type: 'error', title: 'Action failed', description: e?.response?.data?.error || e?.message || 'Unknown error' });
    } finally { setBusy(null); }
  };

  const label = (u: any) => u.type === 'prospecting-seed'
    ? 'Prospecting intelligence'
    : `Call update${u.stage ? ` · stage ${u.stage}` : ''}`;

  const detail = (u: any) => {
    if (u.type === 'prospecting-seed') {
      const s = u.summary || {};
      const bits = [s.companyName, s.primaryPainAxis, s.maturityBand].filter(Boolean).join(' · ');
      const icp = s.icpScore != null ? `, ICP ${s.icpScore}` : '';
      return `${bits}${icp} — fills ${u.suggestedStages?.length || 0} stages`;
    }
    const f = u.fieldUpdates || {};
    const keys = Object.keys(f).slice(0, 4);
    const adv = u.stageAdvancement;
    const updates = keys.length ? `Updates ${keys.join(', ')}. ` : '';
    return `${updates}${adv?.direction === 'forward' ? `Advances to stage ${adv.newStage}.` : ''}`.trim() || 'Stage update';
  };

  return (
    <div className="mb-6 rounded-xl border-2 border-violet-300 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-violet-600" />
        <p className="text-sm font-bold text-violet-900 dark:text-violet-200">
          {pending.length} suggestion{pending.length !== 1 ? 's' : ''} awaiting review
        </p>
        <Badge variant="active" className="text-[10px]">MDA validation</Badge>
      </div>
      <div className="space-y-2">
        {pending.map((u: any) => (
          <div key={u.id} className="flex items-start justify-between gap-3 rounded-lg bg-background border p-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold">{label(u)}</p>
              <p className="text-xs text-muted-foreground mt-0.5 break-words">{detail(u)}</p>
              {u.source && <p className="text-[10px] text-muted-foreground mt-1">source: {u.source}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" onClick={() => act(u.id, 'accept')} disabled={!!busy}>
                <Check className="h-3.5 w-3.5 mr-1" />{busy === u.id + 'accept' ? '…' : 'Accept'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => act(u.id, 'reject')} disabled={!!busy}>
                {busy === u.id + 'reject' ? '…' : 'Dismiss'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Vibe Intel panel (pre-seeded data, read-only reference) ───────────────────
function VibeIntelBanner({ stage, stageData }: { stage: number; stageData: Record<number, any> }) {
  const [open, setOpen] = useState(false);
  const s  = stageData?.[stage]  || {};
  const s1 = stageData?.[1]      || {};
  if (!s.vibeProspectingSource && !s1.vibeProspectingSource) return null;

  const d = stage === 1 ? s : { ...s1, ...s };
  const hasContent = d.icpScore || d.painAxis || d.maturityBand || d.winTheme || s1.signals?.length;
  if (!hasContent) return null;

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 overflow-hidden">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-primary/10 transition-colors">
        <div className="flex items-center gap-2">
          <Target className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-semibold text-primary">Vibe Prospecting Intelligence</span>
          <div className="flex gap-1">
            <IcpPill score={d.icpScore} />
            <PainPill axis={d.painAxis || d.primaryPainAxis} />
            <MaturityPill band={d.maturityBand || d.currentMaturityBand} />
          </div>
        </div>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-primary" /> : <ChevronDown className="h-3.5 w-3.5 text-primary" />}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2 border-t border-primary/10 pt-2">
          {d.winTheme && <blockquote className="border-l-2 border-primary/40 pl-3 text-xs text-muted-foreground italic">{d.winTheme}</blockquote>}
          <CasePills cases={d.cantieroCases} />
          <SignalList signals={s1.signals} label="Buying signals" />
          <TagRow tags={d.architectureTags} mono />
          {s1.sourceUrls?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {s1.sourceUrls.slice(0, 3).map((url: string, i: number) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline truncate max-w-xs">
                  <ExternalLink className="h-2.5 w-2.5 shrink-0" />{url.replace('https://', '').slice(0, 50)}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface Account { id: string; name: string; industryVertical?: string; accountType?: string; plantType?: string; analyzedPlantType?: string; }
interface Contact { id: string; name: string; accountId: string; account_id?: string; role?: string; email?: string; firstName?: string; lastName?: string; }
interface Magnet  { id: string; title: string; type: string; journeySteps?: (string | number)[]; applicableStages?: (string | number)[]; }
// Returns true if a magnet is assigned to the given journey stage.
function magnetMatchesStage(m: Magnet, stage: number): boolean {
  const raw = [...(m.journeySteps || []), ...(m.applicableStages || [])];
  return raw.map(x => Number(x)).includes(stage);
}
interface JourneyData {
  id?: string;
  accountId?: string; contactId?: string; currentStage: number;
  journeyType?: string;
  completedStages: number[]; stageData: Record<number, Record<string, any>>;
}

// ── Stage form — one generic form with per-field suggestions ──────────────────
function StageForm({
  stage, formData, setFormData, journeyId, journeyData, isPartner, magnets,
  guidance, agentLoading, agentError, agentRan, onRunAgent,
  agentNote, setAgentNote, contactId, contactName, contactEmail, accountName, onTranscriptReady,
}: {
  stage: number;
  formData: Record<string, any>;
  setFormData: (fn: (p: Record<string, any>) => Record<string, any>) => void;
  journeyId: string;
  journeyData: JourneyData;
  isPartner?: boolean;
  magnets: Magnet[];
  contactId?: string;
  contactName?: string;
  contactEmail?: string | null;
  accountName?: string;
  onTranscriptReady?: (transcript: string, summary?: string) => void;
  guidance: any;
  agentLoading: boolean;
  agentError: string;
  agentRan: boolean;
  onRunAgent: () => void;
  agentNote: string;
  setAgentNote: (v: string) => void;
}) {
  const fieldSuggestions = STAGE_FIELD_SUGGESTIONS[stage] || {};
  const fieldMeta: Record<string, any> = (journeyData.stageData?.[stage] as any)?._meta || (journeyData.stageData?.[String(stage)] as any)?._meta || {};
  const set = (key: string) => (val: string) => setFormData(p => ({ ...p, [key]: val }));
  const [sendMagnetOpen, setSendMagnetOpen] = useState(false);

  // Live magnet engagement for this stage's magnet, read from the same timeline
  // the Interaction Timeline uses. Shows real Delivered/Opened/Clicked status and
  // pre-fills the editable Delivery Date + Engagement fields (without clobbering manual edits).
  const [magnetStatus, setMagnetStatus] = useState<{ label: string; date: string; engagement: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const mid = formData.magnetId;
    if (!mid || !contactId) { setMagnetStatus(null); return; }
    (async () => {
      try {
        const { data } = await apiClient.get(`/api/contacts/${contactId}/timeline`);
        const events = data.data || data.events || data.timeline || (Array.isArray(data) ? data : []);
        const mine = events.filter((e: any) => e?.metadata?.magnetId === mid);
        if (!mine.length) { if (!cancelled) setMagnetStatus(null); return; }
        // rank: delivered < opened < clicked < downloaded < submitted
        const RANK: Record<string, number> = { DELIVERY: 1, SENT: 1, OPEN: 2, CLICK: 3, DOWNLOAD: 4, FORM_SUBMIT: 5 };
        const ENG: Record<string, string> = { 1: 'delivered', 2: 'opened', 3: 'clicked', 4: 'downloaded', 5: 'downloaded' };
        let best = 0, bestTs = '';
        for (const e of mine) {
          const key = (e.subType || e.type || '').toUpperCase().replace('MAGNET_', '');
          const r = RANK[key] || (e.type === 'MAGNET_DELIVERY' ? 1 : 0);
          if (r > best || (r === best && e.timestamp > bestTs)) { best = r; bestTs = e.timestamp; }
        }
        if (!best) { if (!cancelled) setMagnetStatus(null); return; }
        const label = ['', 'Delivered', 'Opened', 'Clicked', 'Downloaded', 'Submitted'][best];
        const dateIso = (bestTs || '').slice(0, 10);
        if (cancelled) return;
        setMagnetStatus({ label, date: dateIso, engagement: ENG[best] });
        // Upgrade editable fields to the timeline's real status. The timeline is the
        // source of truth, so a real "clicked" overrides a stale "delivered"; never downgrade.
        const ENG_RANK: Record<string, number> = { delivered: 1, opened: 2, clicked: 3, downloaded: 4, revisited: 2 };
        setFormData(p => {
          const curRank = ENG_RANK[p.engagement] || 0;
          const newEng  = best > curRank ? ENG[best] : (p.engagement || ENG[best]);
          return { ...p, deliveryDate: p.deliveryDate || dateIso, engagement: newEng };
        });
      } catch { if (!cancelled) setMagnetStatus(null); }
    })();
    return () => { cancelled = true; };
  }, [formData.magnetId, contactId]);

  return (
    <div className="space-y-5">
      {/* Vibe intel banner — collapsed by default */}
      <VibeIntelBanner stage={stage} stageData={journeyData.stageData} />

      {/* Agent run bar */}
      {journeyId && (
        <AgentRunBar
          stage={stage}
          ran={agentRan}
          loading={agentLoading}
          error={agentError}
          onRun={onRunAgent}
          note={agentNote}
          onNoteChange={setAgentNote}
        />
      )}

      {/* AI Calling panel — stages 6 & 12 only */}
      {(stage === 6 || stage === 12) && journeyId && contactId && (
        <CallStagePanel
          stage={stage as 6 | 12}
          journeyId={journeyId}
          contactId={contactId}
          contactName={contactName}
          accountName={accountName}
          onTranscriptReady={onTranscriptReady}
        />
      )}

      {/* Date + Magnet row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-foreground block mb-1.5">Date</label>
          <input type="date" value={formData.dateCompleted || ''}
            onChange={e => setFormData(p => ({ ...p, dateCompleted: e.target.value }))}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-foreground block mb-1.5">Magnet Delivered</label>
          <select value={formData.magnetId || ''}
            onChange={e => setFormData(p => ({ ...p, magnetId: e.target.value }))}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm">
            <option value="">None</option>
            {(() => {
              const suggested = magnets.filter(m => magnetMatchesStage(m, stage));
              const others    = magnets.filter(m => !magnetMatchesStage(m, stage));
              return (
                <>
                  {suggested.length > 0 && (
                    <optgroup label={`★ Suggested for Stage ${stage}`}>
                      {suggested.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                    </optgroup>
                  )}
                  <optgroup label={suggested.length > 0 ? 'All other magnets' : 'All magnets'}>
                    {others.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                  </optgroup>
                </>
              );
            })()}
          </select>
          {formData.magnetId && contactId && (
            <button type="button" onClick={() => setSendMagnetOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
              <Send className="h-3.5 w-3.5" /> Send this magnet now
            </button>
          )}
          {magnetStatus && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              <Check className="h-3.5 w-3.5" />
              {magnetStatus.label}{magnetStatus.date ? ` · ${magnetStatus.date}` : ''} <span className="text-muted-foreground">(from timeline)</span>
            </div>
          )}
        </div>
      </div>

      <SendMagnetModal
        open={sendMagnetOpen}
        onClose={() => setSendMagnetOpen(false)}
        contactId={contactId || ''}
        contactName={contactName || 'this contact'}
        contactEmail={contactEmail}
        currentStage={String(stage)}
        journeyId={journeyId || undefined}
        initialMagnetId={formData.magnetId}
        onSent={() => {
          const today = new Date().toISOString().slice(0, 10);
          setFormData(p => ({
            ...p,
            engagement: p.engagement || 'delivered',
            deliveryDate: p.deliveryDate || today,
            dateCompleted: p.dateCompleted || today,
          }));
        }}
      />

      {formData.magnetId && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">Delivery Date</label>
            <input type="date" value={formData.deliveryDate || ''}
              onChange={e => setFormData(p => ({ ...p, deliveryDate: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground block mb-1.5">Engagement</label>
            <select value={formData.engagement || ''}
              onChange={e => setFormData(p => ({ ...p, engagement: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm">
              <option value="">Select…</option>
              {['delivered','opened','clicked','downloaded','revisited'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* MQL/PQL qualification — structured, scoreable fields */}
      <QualFields
        stage={stage}
        isPartner={isPartner}
        qual={formData.qual || {}}
        onChange={(q) => setFormData(p => ({ ...p, qual: q }))}
      />

      {/* Key Activities — with AI suggestions */}
      <SuggestedField
        label="Key Activities"
        fieldKey="activities"
        value={formData.activities || ''}
        onChange={set('activities')}
        placeholder="What was discussed or shared at this stage?"
        rows={4}
        suggestions={fieldSuggestions.activities || []}
        guidance={guidance}
        onRunAgent={onRunAgent}
        agentLoading={agentLoading}
        meta={fieldMeta.activities}
      />

      {/* Contact Response — with AI suggestions */}
      <SuggestedField
        label="Contact Response"
        fieldKey="response"
        value={formData.response || ''}
        onChange={set('response')}
        placeholder="How did the contact respond?"
        rows={3}
        suggestions={fieldSuggestions.response || []}
        guidance={guidance}
        onRunAgent={onRunAgent}
        agentLoading={agentLoading}
        meta={fieldMeta.response}
      />

      {/* Next Steps — with AI suggestions */}
      <SuggestedField
        label="Next Steps"
        fieldKey="nextSteps"
        value={formData.nextSteps || ''}
        onChange={set('nextSteps')}
        placeholder="What are the agreed next steps?"
        rows={2}
        suggestions={fieldSuggestions.nextSteps || []}
        guidance={guidance}
        onRunAgent={onRunAgent}
        agentLoading={agentLoading}
        meta={fieldMeta.nextSteps}
      />

      {/* Notes — with AI suggestions */}
      <SuggestedField
        label="Notes"
        fieldKey="notes"
        value={formData.notes || ''}
        onChange={set('notes')}
        placeholder="Additional notes, proof points, signals observed"
        rows={3}
        suggestions={fieldSuggestions.notes || []}
        guidance={guidance}
        onRunAgent={onRunAgent}
        agentLoading={agentLoading}
        meta={fieldMeta.notes}
      />
    </div>
  );
}

// ── Stage 1 form — special: account + contact selectors + trigger field ───────
function Stage1Form({
  formData, setFormData, accounts, filteredContacts, selectedAccount,
  selectedContact, setSelectedAccount, setSelectedContact, setJourneyData,
  navigate, contactIdParam, journeyId, journeyData, isPartner, magnets,
  guidance, agentLoading, agentError, agentRan, onRunAgent, agentNote, setAgentNote,
}: any) {
  const fieldSuggestions = STAGE_FIELD_SUGGESTIONS[1] || {};
  const set = (key: string) => (val: string) => setFormData((p: any) => ({ ...p, [key]: val }));

  return (
    <div className="space-y-5">
      <VibeIntelBanner stage={1} stageData={journeyData.stageData} />

      {/* Account */}
      <div>
        <label className="text-sm font-medium text-foreground block mb-1.5">Account *</label>
        <SearchableSelect
          value={formData.accountId || selectedAccount?.id || ''}
          valueLabel={selectedAccount?.name || ''}
          placeholder="Search account…"
          onChange={async (id) => {
            setSelectedContact(null);
            setFormData((p: any) => ({ ...p, accountId: id, contactId: undefined }));
            setJourneyData((p: JourneyData) => ({ ...p, accountId: id, contactId: undefined }));
            if (!id) { setSelectedAccount(null); return; }
            const known = accounts.find((a: Account) => a.id === id);
            if (known) { setSelectedAccount(known); return; }
            try { const { data } = await apiClient.get(`/api/accounts/${id}`); setSelectedAccount(data); }
            catch { setSelectedAccount(null); }
          }}
          fetchOptions={async (q) => {
            const res = await accountsService.getAccounts({ limit: 25, ...(q ? { search: q } : {}) } as any);
            return (res.data || []).map((a: any) => ({ id: a.id, label: a.name, sublabel: [a.industryVertical, a.region].filter(Boolean).join(' · ') }));
          }}
        />
      </div>

      {/* Contact */}
      <div>
        <label className="text-sm font-medium text-foreground block mb-1.5">Contact *</label>
        <SearchableSelect
          value={formData.contactId || selectedContact?.id || ''}
          valueLabel={selectedContact ? ((selectedContact as any).name || `${(selectedContact as any).firstName || ''} ${(selectedContact as any).lastName || ''}`.trim()) : ''}
          placeholder="Search contact…"
          disabled={!selectedAccount}
          onChange={(id, opt) => {
            const c = filteredContacts.find((x: Contact) => x.id === id) || (opt ? ({ id, name: opt.label } as any) : null);
            setSelectedContact(c);
            setFormData((p: any) => ({ ...p, contactId: id }));
            setJourneyData((p: JourneyData) => ({ ...p, contactId: id }));
            if (id && contactIdParam === 'new') navigate(`/abm-journeys/${id}`, { replace: true });
          }}
          fetchOptions={async (q) => {
            if (!selectedAccount) return [];
            const cr = await contactsService.getContacts({ account_id: selectedAccount.id, limit: 50, ...(q ? { search: q } : {}) } as any);
            return (cr.data || []).map((c: any) => ({ id: c.id, label: `${c.firstName} ${c.lastName}`.trim(), sublabel: c.title || c.email }));
          }}
        />
      </div>

      {selectedContact && (
        <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/30 border border-border text-xs">
          <div><span className="text-muted-foreground">Role </span><span className="font-medium">{selectedContact.role || (selectedContact as any).title || '—'}</span></div>
          <div><span className="text-muted-foreground">Email </span><span className="font-medium">{selectedContact.email || '—'}</span></div>
        </div>
      )}

      {/* Agent run bar — only shows once journeyId is known */}
      {journeyId && (
        <AgentRunBar
          stage={1}
          ran={agentRan}
          loading={agentLoading}
          error={agentError}
          onRun={onRunAgent}
          note={agentNote}
          onNoteChange={setAgentNote}
        />
      )}

      {/* Plant Type */}
      <div>
        <label className="text-sm font-medium text-foreground block mb-1.5">Plant Type</label>
        <input type="text" value={formData.plantType ?? (selectedAccount?.plantType || (selectedAccount as any)?.analyzedPlantType || '')}
          onChange={e => setFormData((p: any) => ({ ...p, plantType: e.target.value }))}
          placeholder="e.g. Assembly, Fabrication, Discrete"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm" />
      </div>

      {/* Trigger — with AI suggestion */}
      <SuggestedField
        label="Trigger"
        fieldKey="trigger"
        value={formData.trigger || ''}
        onChange={set('trigger')}
        placeholder="What triggered this engagement? (AI will suggest based on buying signals)"
        rows={3}
        suggestions={fieldSuggestions.trigger || []}
        guidance={guidance}
        onRunAgent={onRunAgent}
        agentLoading={agentLoading}
      />

      {/* MQL/PQL qualification — structured, scoreable fields */}
      <QualFields
        stage={1}
        isPartner={isPartner}
        qual={formData.qual || {}}
        onChange={(qq: Record<string, any>) => setFormData((p: any) => ({ ...p, qual: qq }))}
      />

      {/* Key Activities — with AI suggestion */}
      <SuggestedField
        label="First Outreach / Key Activities"
        fieldKey="activities"
        value={formData.activities || ''}
        onChange={set('activities')}
        placeholder="What outreach was sent? (AI will suggest email opener)"
        rows={4}
        suggestions={fieldSuggestions.activities || []}
        guidance={guidance}
        onRunAgent={onRunAgent}
        agentLoading={agentLoading}
      />

      {/* Notes — with AI suggestion */}
      <SuggestedField
        label="Notes"
        fieldKey="notes"
        value={formData.notes || ''}
        onChange={set('notes')}
        placeholder="Qualifying questions to ask, additional context"
        rows={3}
        suggestions={fieldSuggestions.notes || []}
        guidance={guidance}
        onRunAgent={onRunAgent}
        agentLoading={agentLoading}
      />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ABMJourneyPage() {
  const navigate = useNavigate();
  const { contactId: contactIdParam } = useParams();
  const { addToast } = useToast();

  const [currentStage, setCurrentStage]   = useState(1);
  const [accounts, setAccounts]           = useState<Account[]>([]);
  const [contacts, setContacts]           = useState<Contact[]>([]);
  const [magnets, setMagnets]             = useState<Magnet[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [selectedAccount, setSelectedAccount]   = useState<Account | null>(null);
  const [selectedContact, setSelectedContact]   = useState<Contact | null>(null);
  const [journeyData, setJourneyData] = useState<JourneyData>({ currentStage: 1, completedStages: [], stageData: {} });
  const isPartner = (journeyData.journeyType || selectedAccount?.accountType || '').toString().toUpperCase() === 'PARTNER';
  const activeStages = isPartner ? PARTNER_JOURNEY_STAGES : JOURNEY_STAGES;
  const [formData, setFormData]   = useState<Record<string, any>>({});
  const [journeyId, setJourneyId] = useState('');
  const [agentNote, setAgentNote] = useState('');

  // Agent state per stage
  const agent = useStageAgent(journeyId, currentStage, journeyData.stageData?.[currentStage]?.agentGuidance);

  const runAgent = () => agent.run(formData, agentNote);

  // Load static data — sequential (not concurrent) to avoid freezing the renderer,
  // with a hard cap of 200 records (dropdown doesn't need all 1000).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ar = await accountsService.getAccounts({ limit: 200 });
        if (cancelled) return;
        setAccounts(ar.data);
        const cr = await contactsService.getContacts({ limit: 200 });
        if (cancelled) return;
        setContacts(cr.data.map((c: any) => ({ ...c, name: `${c.firstName} ${c.lastName}`, accountId: c.accountId || c.account_id })));
        const mr = await apiClient.get('/api/magnets?limit=200');
        if (cancelled) return;
        setMagnets((mr.data?.data || []).map((m) => ({
          ...m,
          journeySteps: m.journeySteps || m.applicableStages || m.targetStages || [],
          applicableStages: m.applicableStages || m.targetStages || [],
        })));
      } catch { if (!cancelled) addToast({ type: 'error', title: 'Error', description: 'Failed to load data' }); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load journey from URL param (also called after accepting a suggestion)
  const loadJourney = useCallback(async () => {
    if (!contactIdParam || contactIdParam === 'new') return;
    try {
      const { data: parsed } = await apiClient.get(`/api/abm-journeys/contact/${contactIdParam}`, { timeout: 15000 });
      if (parsed && parsed.id) {
        setJourneyData(parsed);
        setJourneyId(parsed.id || '');
        const cs = parsed.currentStage || 1;
        setCurrentStage(cs);
        setFormData(parsed.stageData?.[cs] || parsed.stageData?.[String(cs)] || {});
        return;
      }
      // No journey exists for this contact. Do NOT auto-create — redirect to the
      // contact's account so the user can start it explicitly from there.
      try {
        const { data: contact } = await apiClient.get(`/api/contacts/${contactIdParam}`);
        const accId = contact?.accountId || contact?.account_id;
        addToast({ type: 'info', title: 'No ABM journey yet', description: 'Start the journey from the account page.' });
        navigate(accId ? `/accounts/${accId}` : '/accounts', { replace: true });
      } catch {
        navigate('/accounts', { replace: true });
      }
    } catch {
      // GET failed (e.g. 404 = no journey). Same rule: redirect to the account.
      try {
        const { data: contact } = await apiClient.get(`/api/contacts/${contactIdParam}`);
        const accId = contact?.accountId || contact?.account_id;
        addToast({ type: 'info', title: 'No ABM journey yet', description: 'Start the journey from the account page.' });
        navigate(accId ? `/accounts/${accId}` : '/accounts', { replace: true });
      } catch {
        navigate('/accounts', { replace: true });
      }
    }
  }, [contactIdParam, navigate, addToast]);
  useEffect(() => { loadJourney(); }, [loadJourney]);

  // Sync account/contact from journey. The global lists are capped, so a
  // journey's account/contact may not be present — in that case fetch it
  // directly. NEVER null out an already-selected value on a miss (that was
  // wiping the Account/Contact fields on save/continue).
  useEffect(() => {
    const accId = journeyData.accountId;
    if (!accId) return;
    if (selectedAccount?.id === accId) return;
    const known = accounts.find(a => a.id === accId);
    if (known) { setSelectedAccount(known); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/api/accounts/${accId}`);
        if (!cancelled && data) setSelectedAccount(data);
      } catch { /* leave existing selection intact */ }
    })();
    return () => { cancelled = true; };
  }, [journeyData.accountId, accounts]);

  useEffect(() => {
    const cId = journeyData.contactId;
    if (!cId) return;
    if (selectedContact?.id === cId) return;
    const known = contacts.find(c => c.id === cId);
    if (known) { setSelectedContact(known); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/api/contacts/${cId}`);
        if (!cancelled && data) {
          const mapped = { ...data, name: `${data.firstName || ''} ${data.lastName || ''}`.trim(), accountId: data.accountId || data.account_id };
          setSelectedContact(mapped);
          setContacts(prev => prev.some(c => c.id === mapped.id) ? prev : [...prev, mapped]);
        }
      } catch { /* leave existing selection intact */ }
    })();
    return () => { cancelled = true; };
  }, [journeyData.contactId, contacts]);

  // Auto-set contact from URL
  useEffect(() => {
    if (!contactIdParam || journeyData.contactId || !contacts.length) return;
    const cont = contacts.find(c => c.id === contactIdParam);
    if (cont) {
      setSelectedContact(cont);
      const accId = cont.accountId || cont.account_id;
      setJourneyData(p => ({ ...p, contactId: contactIdParam, accountId: accId }));
      setFormData(p => ({ ...p, contactId: contactIdParam, accountId: accId }));
      if (accId) setSelectedAccount(accounts.find(a => a.id === accId) || null);
    }
  }, [contactIdParam, contacts, accounts]);

  useEffect(() => {
    if (!selectedAccount) { setFilteredContacts([]); return; }
    // Fetch this account's contacts directly — the global list is capped, so
    // an account's contacts may not be in it once there are many contacts.
    let cancelled = false;
    (async () => {
      try {
        const cr = await contactsService.getContacts({ account_id: selectedAccount.id, limit: 500 });
        const mapped = cr.data.map((c: any) => ({ ...c, name: `${c.firstName || ''} ${c.lastName || ''}`.trim(), accountId: c.accountId || c.account_id }));
        if (cancelled) return;
        setFilteredContacts(mapped);
        // Merge into the global contacts list so downstream lookups (pre-select
        // by journey contactId) resolve too.
        setContacts(prev => {
          const ids = new Set(prev.map(c => c.id));
          const add = mapped.filter((c: Contact) => !ids.has(c.id));
          return add.length ? [...prev, ...add] : prev;
        });
      } catch {
        if (!cancelled) setFilteredContacts(contacts.filter(c => c.accountId === selectedAccount.id || c.account_id === selectedAccount.id));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAccount]);

  // Reset agent note and load saved guidance when switching stages
  useEffect(() => {
    setAgentNote('');
    const saved = journeyData.stageData?.[currentStage]?.agentGuidance;
    if (saved) agent.setGuidance(saved);
    else agent.setGuidance(null);
  }, [currentStage]);

  const persist = async (updated: JourneyData) => {
    // Fall back to the currently selected account/contact and the URL param so a
    // transient empty field never silently drops the save.
    const contactId = updated.contactId || selectedContact?.id || contactIdParam;
    const accountId = updated.accountId || selectedAccount?.id
      || (selectedContact as any)?.accountId || (selectedContact as any)?.account_id;
    if (!contactId || !accountId) {
      addToast({ type: 'error', title: 'Cannot save', description: 'Account and Contact are required. Pick them in Stage 1 first.' });
      return null;
    }
    localStorage.setItem(`abm-journey-${contactId}`, JSON.stringify(updated));
    try {
      const { data: saved } = await apiClient.post('/api/abm-journeys', {
        contactId, accountId,
        currentStage: updated.currentStage, completedStages: updated.completedStages, stageData: updated.stageData,
      });
      return saved || null;  // server response carries per-field _meta
    } catch (e: any) {
      addToast({ type: 'error', title: 'Save failed', description: e?.response?.data?.message || e?.response?.data?.error || e.message });
      return null;
    }
  };

  const save = async () => {
    const stageWithGuidance = { ...formData };
    if (agent.guidance) stageWithGuidance.agentGuidance = agent.guidance;
    const u: JourneyData = { ...journeyData, currentStage, stageData: { ...journeyData.stageData, [currentStage]: stageWithGuidance } };
    setJourneyData(u);
    const saved = await persist(u);
    if (saved && saved.stageData) {
      // Refresh journeyData with the server copy (now includes per-field _meta)
      setJourneyData(prev => ({ ...prev, ...saved }));
    }
    addToast({ type: 'success', title: 'Saved', description: `Stage ${currentStage} saved` });
  };

  const complete = async () => {
    const stageWithGuidance = { ...formData, completedAt: new Date().toISOString() };
    if (agent.guidance) stageWithGuidance.agentGuidance = agent.guidance;
    const u: JourneyData = {
      ...journeyData,
      completedStages: [...new Set([...journeyData.completedStages, currentStage])],
      currentStage: Math.min(currentStage + 1, 14),
      stageData: { ...journeyData.stageData, [currentStage]: stageWithGuidance },
    };
    setJourneyData(u);
    await persist(u);
    if (currentStage < 14) {
      const next = currentStage + 1;
      setCurrentStage(next);
      setFormData(u.stageData[next] || {});
      agent.setGuidance(u.stageData[next]?.agentGuidance || null);
    }
    addToast({ type: 'success', title: 'Stage complete', description: `Stage ${currentStage} marked done` });
  };

  const goTo = (id: number) => {
    // Save current stage data in memory before switching (no API call — user hits Save explicitly)
    const currentData = { ...formData };
    if (agent.guidance) currentData.agentGuidance = agent.guidance;
    setJourneyData(p => ({ ...p, stageData: { ...p.stageData, [currentStage]: currentData } }));
    // Switch to new stage
    setCurrentStage(id);
    const existing = journeyData.stageData[id] || journeyData.stageData[String(id)] || {};
    setFormData(existing);
    agent.setGuidance(existing.agentGuidance || null);
  };

  const s1 = journeyData.stageData?.[1] || {};

  // Handle transcript from AI call → auto-fill stage fields via Voice Agent
  const handleTranscriptReady = async (transcript: string, summary?: string) => {
    if (!journeyId || !journeyData.contactId) return;
    try {
      const { data } = await apiClient.post('/api/voice-agent/extract-stage-data', {
        contactId: journeyData.contactId,
        journeyId,
        stage: currentStage,
        rawNotes: transcript,
        userNote: summary ? `Call summary: ${summary}` : undefined,
      });
      if (data?.extracted) {
        const ex = data.extracted;
        setFormData((p: Record<string, any>) => ({
          ...p,
          ...(ex.activities && !p.activities ? { activities: ex.activities } : {}),
          ...(ex.response   && !p.response   ? { response:   ex.response   } : {}),
          ...(ex.nextSteps  && !p.nextSteps  ? { nextSteps:  ex.nextSteps  } : {}),
          ...(ex.notes      && !p.notes      ? { notes:      ex.notes      } : {}),
        }));
        addToast({ type: 'success', title: 'Form auto-filled', description: 'Stage fields populated from call transcript.' });
      }
    } catch { /* non-fatal — user can fill manually */ }
  };

  const commonFormProps = {
    stage: currentStage,
    formData,
    setFormData,
    journeyId,
    journeyData,
    isPartner,
    magnets,
    guidance: agent.guidance,
    agentLoading: agent.loading,
    agentError: agent.error,
    agentRan: agent.ran,
    onRunAgent: runAgent,
    agentNote,
    setAgentNote,
    contactId: journeyData.contactId || contactIdParam,
    contactName: selectedContact?.name,
    contactEmail: selectedContact?.email,
    accountName: selectedAccount?.name,
    onTranscriptReady: handleTranscriptReady,
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* ── Sidebar ── */}
      <div className="w-72 border-r bg-muted/30 overflow-y-auto shrink-0">
        <div className="p-5 border-b space-y-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />Back
          </Button>
          <div>
            <h2 className="text-base font-semibold">ABM 14-Step Journey</h2>
            <p className="text-xs text-muted-foreground">{isPartner ? 'Partner → PQL' : 'Contact → MQL'}</p>
          </div>

          {(selectedAccount || selectedContact) && (
            <div className="p-3 bg-background rounded-lg border space-y-1.5">
              {selectedAccount && <div><p className="text-[10px] text-muted-foreground">Account</p><p className="text-sm font-medium">{selectedAccount.name}</p></div>}
              {selectedContact && <div><p className="text-[10px] text-muted-foreground">Contact</p><p className="text-sm font-medium">{selectedContact.name}</p></div>}
              {s1.vibeProspectingSource && (
                <div className="flex flex-wrap gap-1 pt-1 border-t border-border">
                  <PainPill axis={s1.painAxis} /><MaturityPill band={s1.maturityBand} /><IcpPill score={s1.icpScore} />
                </div>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Progress</span>
              <span className="text-xs font-bold">{journeyData.completedStages.length}/14</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5 mt-1">
              <div className="bg-primary h-1.5 rounded-full transition-all" style={{ width: `${(journeyData.completedStages.length / 14) * 100}%` }} />
            </div>
          </div>

          {journeyData.contactId && (
            <EngagementScoreWidget
              contactId={journeyData.contactId}
              compact
              onReady={() => addToast({ type: 'success', title: isPartner ? 'PQL Ready!' : 'MQL Ready!', description: isPartner ? 'Partner reached PQL threshold.' : 'Contact reached MQL threshold.' })}
            />
          )}
        </div>

        {/* Stage list */}
        <div className="p-3">
          {activeStages.map((stage, i) => {
            const done = journeyData.completedStages.includes(stage.id);
            const cur  = currentStage === stage.id;
            // Allow: completed stages, current stage, and one stage ahead (next stage)
            const maxReached = Math.max(...(journeyData.completedStages.length ? journeyData.completedStages : [0]), currentStage);
            const ok   = stage.id <= maxReached + 1;
            const hasGuidance = !!(journeyData.stageData?.[stage.id]?.agentGuidance);
            return (
              <div key={stage.id} className="relative">
                {i < 13 && <div className={`absolute left-4 top-10 w-0.5 h-14 ${done ? 'bg-primary' : 'bg-border'}`} />}
                <button onClick={() => ok && goTo(stage.id)} disabled={!ok}
                  className={`w-full text-left p-2.5 rounded-lg mb-1.5 transition-colors ${
                    cur ? 'bg-primary/10 border-2 border-primary' : done ? 'bg-muted hover:bg-muted/80' : ok ? 'hover:bg-muted/50' : 'opacity-40 cursor-not-allowed'
                  }`}>
                  <div className="flex items-start gap-2.5">
                    <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      done ? 'bg-primary text-primary-foreground' : cur ? 'bg-primary/20 text-primary' : 'bg-muted-foreground/20 text-muted-foreground'
                    }`}>
                      {done ? <Check className="h-3.5 w-3.5" /> : stage.id}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Badge variant={PHASE_COLORS[stage.phase as keyof typeof PHASE_COLORS]} className="text-[9px] py-0">{stage.phase}</Badge>
                        {hasGuidance && <Sparkles className="h-2.5 w-2.5 text-violet-500" title="AI guidance saved" />}
                      </div>
                      <p className="text-xs font-medium leading-tight">{stage.title}</p>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-3xl mx-auto">
          <PendingSuggestionsBanner journeyId={journeyId} onApplied={loadJourney} addToast={addToast} />
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={PHASE_COLORS[activeStages[currentStage - 1].phase as keyof typeof PHASE_COLORS]}>
                  {activeStages[currentStage - 1].phase}
                </Badge>
                <span className="text-xs text-muted-foreground">Stage {currentStage} of 14</span>
              </div>
              <CardTitle className="text-xl">{currentStage}. {activeStages[currentStage - 1].title}</CardTitle>
              <p className="text-sm text-muted-foreground">{activeStages[currentStage - 1].desc}</p>
            </CardHeader>
            <CardContent>
              {currentStage === 1 ? (
                <Stage1Form
                  {...commonFormProps}
                  accounts={accounts}
                  filteredContacts={filteredContacts}
                  selectedAccount={selectedAccount}
                  selectedContact={selectedContact}
                  setSelectedAccount={setSelectedAccount}
                  setSelectedContact={setSelectedContact}
                  setJourneyData={setJourneyData}
                  navigate={navigate}
                  contactIdParam={contactIdParam}
                />
              ) : (
                <StageForm {...commonFormProps} />
              )}

              <StageRemarks
                key={currentStage}
                journeyId={journeyId}
                stageN={currentStage}
                initial={journeyData.stageData?._stageRemarks?.[currentStage] || []}
              />

              <div className="flex gap-2 mt-6 pt-6 border-t border-border">
                <Button variant="outline" onClick={save}>
                  <Save className="h-4 w-4 mr-2" />Save
                </Button>
                <Button onClick={complete}
                  disabled={currentStage === 1 && (!selectedAccount || !selectedContact)}>
                  {currentStage < 14 ? 'Complete & Continue →' : 'Complete Stage'}
                </Button>
                {currentStage === 14 && journeyData.completedStages.length === 14 && (
                  <Button variant="outline" onClick={() => navigate('/mql')}>Submit MQL</Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
